import { Calendar } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { de, enUS, es } from 'date-fns/locale';
import { format, parse, startOfDay } from 'date-fns';
import 'react-day-picker/style.css';

function parseLocalYmd(s) {
  if (!s) return undefined;
  return parse(s, 'yyyy-MM-dd', new Date());
}

function toYmd(d) {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Selector de rango con dos meses (react-day-picker). Valores `from` / `to` en yyyy-MM-dd.
 */
export default function DateRangeFilter({
  from,
  to,
  onRangeChange,
  label,
  idPrefix,
  emptySummary = 'Todas las fechas',
  dialogAriaLabel,
  /** Oculta el botón "Quitar rango" (p. ej. cuando el rango es obligatorio). */
  hideClearButton = false,
  /** Deshabilita días anteriores a esta fecha (inicio del día local). */
  disabledBefore = null,
  labelClassName = '',
  /** Días cerrados (yyyy-MM-dd): no se pueden elegir como inicio ni fin del rango. */
  closedDates = null,
  /** Locale de date-fns / calendario (por defecto español). */
  locale: localeProp = es
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerId = `${idPrefix}-range-trigger`;
  const ariaLabel = dialogAriaLabel ?? label;
  const locale = localeProp ?? es;

  /**
   * Rango en curso mientras el usuario elige inicio y fin (el padre puede ignorar el primer clic
   * si solo envía `from`, y sin estado local el DayPicker controlado no permite completar el rango).
   */
  const [pendingRange, setPendingRange] = useState(undefined);

  const partialLabels = useMemo(() => {
    if (locale === enUS || locale?.code === 'en-US') {
      return { from: 'From', until: 'Until' };
    }
    if (locale === de || locale?.code === 'de') {
      return { from: 'Ab', until: 'Bis' };
    }
    return { from: 'Desde', until: 'Hasta' };
  }, [locale]);

  const selectedFromProps = useMemo(() => {
    const fromD = parseLocalYmd(from);
    const toD = parseLocalYmd(to);
    if (!fromD && !toD) return undefined;
    if (fromD && toD) {
      if (toD < fromD) return { from: toD, to: fromD };
      return { from: fromD, to: toD };
    }
    if (fromD) return { from: fromD, to: undefined };
    return { from: toD, to: toD };
  }, [from, to]);

  const selected = pendingRange ?? selectedFromProps;

  const summary = useMemo(() => {
    if (!from && !to) return emptySummary;
    const fmt = (s) => (s ? format(parseLocalYmd(s), 'd MMM yyyy', { locale }) : '');
    if (from && to) return `${fmt(from)} — ${fmt(to)}`;
    if (from) return `${partialLabels.from} ${fmt(from)}`;
    return `${partialLabels.until} ${fmt(to)}`;
  }, [from, to, emptySummary, locale, partialLabels]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      const root = containerRef.current;
      if (!root) return;
      const path = typeof e.composedPath === 'function' ? e.composedPath() : [];
      const inside =
        path.length > 0
          ? path.includes(root)
          : e.target instanceof Node && root.contains(e.target);
      if (!inside) setOpen(false);
    };
    // click: evita interferencias con el ciclo mousedown→focus→mouseup del calendario en modales
    document.addEventListener('click', onDoc, true);
    return () => document.removeEventListener('click', onDoc, true);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setPendingRange(undefined);
    }
  }, [open]);

  const handleSelect = (range) => {
    if (!range) {
      onRangeChange('', '');
      setPendingRange(undefined);
      return;
    }
    const nextFrom = range.from ? toYmd(range.from) : '';
    const nextTo = range.to ? toYmd(range.to) : '';

    if (nextFrom && nextTo) {
      setPendingRange(undefined);
      onRangeChange(nextFrom, nextTo);
      return;
    }

    if (nextFrom && !nextTo) {
      setPendingRange({ from: range.from, to: undefined });
      onRangeChange(nextFrom, '');
      return;
    }

    onRangeChange(nextFrom, nextTo);
  };

  const clearRange = () => {
    setPendingRange(undefined);
    onRangeChange('', '');
  };

  const defaultMonth = selected?.from ?? selected?.to ?? new Date();

  useEffect(() => {
    setPendingRange(undefined);
  }, [from, to]);

  const closedDayDates = useMemo(
    () => (closedDates || []).map((d) => parseLocalYmd(d)).filter(Boolean),
    [closedDates]
  );

  const disabledMatchers = [
    ...(disabledBefore ? [{ before: startOfDay(disabledBefore) }] : []),
    ...closedDayDates,
  ];
  const disabledMatcher = disabledMatchers.length > 0 ? disabledMatchers : undefined;

  return (
    <div
      className="date-range-filter filter-group"
      ref={containerRef}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <label htmlFor={triggerId} className={labelClassName || undefined}>{label}</label>
      <button
        id={triggerId}
        type="button"
        className="date-range-trigger text-black"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <Calendar size={16} aria-hidden />
        <span className="date-range-summary">{summary}</span>
      </button>
      {open && (
        <div className="date-range-dropdown" role="dialog" aria-label={ariaLabel}>
          <DayPicker
            mode="range"
            numberOfMonths={2}
            selected={selected}
            onSelect={handleSelect}
            locale={locale}
            defaultMonth={defaultMonth}
            weekStartsOn={1}
            disabled={disabledMatcher}
            modifiers={{ closed: closedDayDates }}
            modifiersClassNames={{ closed: 'date-range-day-closed' }}
            /** Con rango ya elegido, el 1.er clic debe ser solo inicio (no recortar/mover fin en un solo clic). */
            resetOnSelect
          />
          {!hideClearButton && (
            <div className="date-range-dropdown-footer">
              <button type="button" className="btn-secondary date-range-clear" onClick={clearRange}>
                Quitar rango
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
